import { useState, useEffect, useRef } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Upload, Loader2, ShieldCheck, CheckCircle, Camera, FolderOpen, ScanFace } from 'lucide-react';
import { SelfieLivenessCheck } from '@/components/SelfieLivenessCheck';

export function KycFormSection({ clientId }) {
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [livenessOpen, setLivenessOpen] = useState(false);
  // Each doc gets a camera-input ref AND a gallery-input ref
  const refs = {
    pan_card:   { cam: useRef(null), gal: useRef(null) },
    aadhaar:    { cam: useRef(null), gal: useRef(null) },
    photo:      { cam: useRef(null), gal: useRef(null) },
    signature:  { cam: useRef(null), gal: useRef(null) },
  };

  useEffect(() => {
    api.get(`/kyc/client/${clientId}`).then(res => {
      setKyc(res.data);
      setForm(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/kyc/submit', { ...form, client_id: clientId });
      toast.success('KYC submitted');
      const res = await api.get(`/kyc/client/${clientId}`);
      setKyc(res.data); setForm(res.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadBlob(file, docType);
    // Reset the input so re-selecting same file fires onChange
    e.target.value = '';
  };

  const uploadBlob = async (blob, docType, filename) => {
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('client_id', clientId);
    formData.append('doc_type', docType);
    formData.append('file', blob, filename || (blob.name || `${docType}.jpg`));
    try {
      await api.post('/kyc/upload-doc', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${docType.replace('_', ' ')} uploaded`);
      const res = await api.get(`/kyc/client/${clientId}`);
      setKyc(res.data); setForm(res.data);
    } catch (err) {
      console.error('[KYC] upload failed:', err);
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleLivenessCapture = async (blob) => {
    await uploadBlob(blob, 'photo', `selfie-${Date.now()}.jpg`);
    // Mark liveness completed
    setForm(prev => ({ ...prev, liveness_verified: true, liveness_verified_at: new Date().toISOString() }));
  };

  const f = (key) => form[key] || '';
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-brand-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="kyc-form-section">
      {kyc?.status && kyc.status !== 'not_submitted' && (
        <Badge className={`text-xs border-0 px-3 py-1 ${kyc.status === 'verified' ? 'bg-brand-success/15 text-brand-success' : kyc.status === 'rejected' ? 'bg-red-100 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>
          KYC: {kyc.status} {kyc.status === 'verified' && <CheckCircle size={12} className="ml-1" />}
        </Badge>
      )}

      {/* Personal Details */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Personal Details</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Full Name</Label><Input value={f('full_name')} onChange={e => set('full_name', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" data-testid="kyc-full-name" /></div>
          <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={f('date_of_birth')} onChange={e => set('date_of_birth', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" data-testid="kyc-dob" /></div>
          <div><Label className="text-xs">Father Name</Label><Input value={f('father_name')} onChange={e => set('father_name', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Mother Name</Label><Input value={f('mother_name')} onChange={e => set('mother_name', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={f('phone')} onChange={e => set('phone', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Email</Label><Input value={f('email')} onChange={e => set('email', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
        </CardContent>
      </Card>

      {/* Identity Details */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Identity & Address</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">PAN Number</Label><Input value={f('pan_number')} onChange={e => set('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" data-testid="kyc-pan" /></div>
          <div><Label className="text-xs">Aadhaar Number</Label><Input value={f('aadhaar_number')} onChange={e => set('aadhaar_number', e.target.value)} placeholder="1234 5678 9012" className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" data-testid="kyc-aadhaar" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={f('address')} onChange={e => set('address', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">City</Label><Input value={f('city')} onChange={e => set('city', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">State</Label><Input value={f('state')} onChange={e => set('state', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">PIN Code</Label><Input value={f('pin_code')} onChange={e => set('pin_code', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" /></div>
        </CardContent>
      </Card>

      {/* Nominee */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Nominee Details</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label className="text-xs">Nominee Name</Label><Input value={f('nominee_name')} onChange={e => set('nominee_name', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Nominee DOB</Label><Input type="date" value={f('nominee_dob')} onChange={e => set('nominee_dob', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Relationship</Label>
            <Select value={f('nominee_relationship')} onValueChange={v => set('nominee_relationship', v)}>
              <SelectTrigger className="bg-white border-brand-border rounded-lg text-sm h-9"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{['Spouse', 'Father', 'Mother', 'Son', 'Daughter', 'Brother', 'Sister', 'Other'].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Financial */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Financial Details</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Income Source</Label><Input value={f('income_source')} onChange={e => set('income_source', e.target.value)} placeholder="Business, Salary, etc." className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
          <div><Label className="text-xs">Occupation</Label><Input value={f('occupation')} onChange={e => set('occupation', e.target.value)} className="bg-white border-brand-border rounded-lg text-sm h-9" /></div>
        </CardContent>
      </Card>

      {/* Bank / UPI (for automated payouts) */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Bank & UPI (for payouts)</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Account Number</Label><Input value={f('bank_account_number')} onChange={e => set('bank_account_number', e.target.value.replace(/\s/g, ''))} placeholder="12345678901234" className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" data-testid="kyc-bank-acct" /></div>
          <div><Label className="text-xs">IFSC Code</Label><Input value={f('bank_ifsc')} onChange={e => set('bank_ifsc', e.target.value.toUpperCase())} placeholder="HDFC0001234" className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" data-testid="kyc-bank-ifsc" /></div>
          <div><Label className="text-xs">Account Holder Name</Label><Input value={f('bank_holder_name')} onChange={e => set('bank_holder_name', e.target.value)} placeholder="As on passbook" className="bg-white border-brand-border rounded-lg text-sm h-9" data-testid="kyc-bank-holder" /></div>
          <div><Label className="text-xs">Bank Name</Label><Input value={f('bank_name')} onChange={e => set('bank_name', e.target.value)} placeholder="HDFC Bank" className="bg-white border-brand-border rounded-lg text-sm h-9" data-testid="kyc-bank-name" /></div>
          <div><Label className="text-xs">Bank Branch</Label><Input value={f('bank_branch')} onChange={e => set('bank_branch', e.target.value)} placeholder="Kolkata - Salt Lake" className="bg-white border-brand-border rounded-lg text-sm h-9" data-testid="kyc-bank-branch" /></div>
          <div><Label className="text-xs">UPI ID</Label><Input value={f('upi_id')} onChange={e => set('upi_id', e.target.value.toLowerCase())} placeholder="name@upi" className="bg-white border-brand-border rounded-lg text-sm h-9 font-mono" data-testid="kyc-upi-id" /></div>
        </CardContent>
      </Card>

      {/* Document Uploads */}
      <Card className="border border-brand-border bg-white">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-brand-text-primary">Documents</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['PAN Card', 'pan_card', 'pan_card_doc'],
            ['Aadhaar Card', 'aadhaar', 'aadhaar_doc'],
            ['Photo / Selfie', 'photo', 'photo_doc'],
            ['Signature', 'signature', 'signature_doc'],
          ].map(([label, docType, field]) => {
            const isPhoto = docType === 'photo';
            const isUploading = uploadingDoc === docType;
            return (
              <div key={docType} className="text-center">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">{label}</p>
                {kyc?.[field] ? (
                  <img src={kyc[field]} alt={label} className="w-full h-20 object-contain border border-brand-border rounded-lg mb-1 bg-white" />
                ) : (
                  <div className="w-full h-20 bg-brand-bg rounded-lg border border-dashed border-brand-border flex items-center justify-center mb-1">
                    {isUploading ? <Loader2 size={16} className="animate-spin text-brand-primary" /> : <Upload size={16} className="text-brand-text-secondary" />}
                  </div>
                )}

                {/* Camera-direct input (capture attribute → opens camera on mobile) */}
                <input
                  ref={refs[docType].cam}
                  type="file"
                  accept="image/*"
                  capture={isPhoto ? 'user' : 'environment'}
                  className="hidden"
                  onChange={e => handleUpload(e, docType)}
                />
                {/* Gallery input (no capture attribute → opens file picker) */}
                <input
                  ref={refs[docType].gal}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => handleUpload(e, docType)}
                />

                <div className="flex flex-col gap-1">
                  {/* For Photo/Selfie show 3 options including liveness */}
                  {isPhoto && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLivenessOpen(true)}
                      disabled={isUploading}
                      className="rounded-lg text-[10px] h-7 w-full bg-brand-primary/10 border-brand-primary/30 text-brand-primary hover:bg-brand-primary/20"
                      data-testid={`liveness-${docType}`}
                    >
                      <ScanFace size={11} className="mr-1" /> Selfie + Liveness
                    </Button>
                  )}
                  <div className="grid grid-cols-2 gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refs[docType].cam.current?.click()}
                      disabled={isUploading}
                      className="rounded-lg text-[10px] h-7 px-1"
                      data-testid={`camera-${docType}`}
                    >
                      <Camera size={11} className="mr-1" /> Camera
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => refs[docType].gal.current?.click()}
                      disabled={isUploading}
                      className="rounded-lg text-[10px] h-7 px-1"
                      data-testid={`gallery-${docType}`}
                    >
                      <FolderOpen size={11} className="mr-1" /> File
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <SelfieLivenessCheck open={livenessOpen} onClose={() => setLivenessOpen(false)} onCapture={handleLivenessCapture} />

      <Button onClick={handleSave} disabled={saving} className="bg-brand-primary text-white rounded-lg" data-testid="kyc-submit-btn">
        {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Submit KYC
      </Button>
    </div>
  );
}
